(() => {
  'use strict';
  const selector = '.ww-card-media>img,.t4s-product-inner .t4s-product-img img.t4s-product-main-img,.t4s-product-inner .t4s-product-img img.t4s-product-hover-img';
  const cache = new Map(), known = new WeakSet(), visible = new WeakSet(), pending = new WeakMap(), fitted = new WeakMap(), requested = new WeakMap();
  const queue = [];
  let running = 0;
  function detectBounds(data, w, h) {
    const white = i => data[i + 3] < 16 || (data[i] > 244 && data[i + 1] > 244 && data[i + 2] > 244);
    let edge = 0, clean = 0;
    for (let x = 0; x < w; x += 3) for (const y of [0, h - 1]) { edge++; if (white((y * w + x) * 4)) clean++; }
    for (let y = 0; y < h; y += 3) for (const x of [0, w - 1]) { edge++; if (white((y * w + x) * 4)) clean++; }
    if (clean / edge < .94) return null;
    const rows = new Uint16Array(h), cols = new Uint16Array(w);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (data[i + 3] > 20 && Math.min(data[i], data[i + 1], data[i + 2]) < 241) { rows[y]++; cols[x]++; }
    }
    let left = w, right = -1, top = h, bottom = -1;
    for (let x = 0; x < w; x++) if (cols[x] >= Math.max(2, h * .008)) { left = Math.min(left, x); right = x; }
    for (let y = 0; y < h; y++) if (rows[y] >= Math.max(2, w * .008)) { top = Math.min(top, y); bottom = y; }
    if (right - left < w * .08 || bottom - top < h * .08) return null;
    left = Math.max(0, left - 3); right = Math.min(w, right + 4);
    top = Math.max(0, top - 3); bottom = Math.min(h, bottom + 4);
    return { x: left / w, y: top / h, w: (right - left) / w, h: (bottom - top) / h };
  }
  function measure(image) {
    const max = Math.max(image.naturalWidth, image.naturalHeight);
    if (!max) return null;
    const w = Math.max(1, Math.round(image.naturalWidth / max * 192));
    const h = Math.max(1, Math.round(image.naturalHeight / max * 192));
    const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, w, h);
    return detectBounds(ctx.getImageData(0, 0, w, h).data, w, h);
  }
  function sourceKey(image) {
    const source = image.currentSrc || image.src;
    if (!source || source.startsWith('data:') || !image.naturalWidth) return '';
    try { const url = new URL(source, location.href); url.searchParams.delete('width'); url.searchParams.delete('height'); return url.href; } catch { return ''; }
  }
  function boundsFor(image, key) {
    if (cache.has(key)) return cache.get(key);
    const result = (async () => {
      try { return measure(image); } catch {}
      return new Promise(resolve => {
        const sample = new Image(); sample.crossOrigin = 'anonymous'; sample.decoding = 'async';
        const timer = setTimeout(() => resolve(null), 5000);
        sample.onload = () => { clearTimeout(timer); try { resolve(measure(sample)); } catch { resolve(null); } };
        sample.onerror = () => { clearTimeout(timer); resolve(null); };
        const url = new URL(key); url.searchParams.set('width', '192'); sample.src = url.href;
      });
    })();
    cache.set(key, result);
    if (cache.size > 300) cache.delete(cache.keys().next().value);
    return result;
  }
  function layoutFor(bounds, width, height, naturalWidth, naturalHeight) {
    if (!bounds || !width || !height || !naturalWidth || !naturalHeight) return null;
    const fit = Math.min(width / naturalWidth, height / naturalHeight);
    const iw = naturalWidth * fit, ih = naturalHeight * fit;
    const inset = Math.max(2, Math.min(width, height) * .0125);
    const scale = Math.min((width - inset * 2) / (bounds.w * iw), (height - inset * 2) / (bounds.h * ih));
    if (scale <= 1.01) return null;
    return { scale, x: (.5 - bounds.x - bounds.w / 2) * iw * scale, y: (.5 - bounds.y - bounds.h / 2) * ih * scale };
  }
  function qualityScale(ideal, width, height, naturalWidth, naturalHeight, dpr) {
    const fit = Math.min(width / naturalWidth, height / naturalHeight);
    return Math.max(1, Math.min(ideal, 1 / (fit * Math.max(1, dpr))));
  }
  function originalWidth(image) {
    const width = Number(image.dataset.sourceWidth || image.getAttribute('width'));
    return width > 0 ? width : Infinity;
  }
  function requestResolution(image, key, width, height, ideal) {
    if (!key || !width || !height || !image.naturalWidth || !image.naturalHeight) return;
    const fit = Math.min(width / image.naturalWidth, height / image.naturalHeight);
    const wanted = Math.min(5760, originalWidth(image), Math.ceil(image.naturalWidth * fit * ideal * Math.max(1, window.devicePixelRatio || 1) / 100) * 100);
    const previous = requested.get(image);
    if (wanted <= image.naturalWidth + 20 || (previous?.key === key && previous.width >= wanted)) return;
    const url = new URL(key); url.searchParams.set('width', String(wanted));
    requested.set(image, { key, width: wanted });
    image.removeAttribute('srcset'); image.removeAttribute('sizes'); image.removeAttribute('data-srcset');
    image.removeAttribute('data-sizes'); image.classList.remove('lazyautosizest4s');
    image.src = url.href;
  }
  function applyFit(image) {
    const bounds = fitted.get(image), frame = image.parentElement;
    if (!frame || !image.complete || !image.naturalWidth || !image.naturalHeight) return;
    const width = frame.clientWidth, height = frame.clientHeight;
    if (!width || !height) return;
    const placement = layoutFor(bounds, width, height, image.naturalWidth, image.naturalHeight);
    const ideal = placement?.scale || 1;
    // Fetch enough source pixels before enlarging. The source file itself is never changed.
    const availableWidth = Math.min(image.naturalWidth, originalWidth(image));
    const scale = qualityScale(ideal, width, height, availableWidth, image.naturalHeight * availableWidth / image.naturalWidth, window.devicePixelRatio || 1);
    if (!placement || scale <= 1.01) {
      image.style.removeProperty('--ww-photo-scale'); image.style.removeProperty('--ww-photo-x'); image.style.removeProperty('--ww-photo-y');
    } else {
      image.style.setProperty('--ww-photo-scale', scale.toFixed(4));
      image.style.setProperty('--ww-photo-x', (placement.x * scale / ideal).toFixed(2) + 'px');
      image.style.setProperty('--ww-photo-y', (placement.y * scale / ideal).toFixed(2) + 'px');
    }
    requestResolution(image, sourceKey(image), width, height, ideal);
  }
  const resize = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    for (const entry of entries) for (const image of entry.target.querySelectorAll('img')) applyFit(image);
  }) : null;
  function drain() {
    while (running < 2 && queue.length) {
      const { image, key } = queue.shift();
      if (!image.isConnected || sourceKey(image) !== key) { if (pending.get(image) === key) pending.delete(image); continue; }
      running++;
      boundsFor(image, key).then(bounds => {
        if (!image.isConnected || sourceKey(image) !== key) return;
        fitted.set(image, bounds); applyFit(image); if (resize && image.parentElement) resize.observe(image.parentElement);
      }).catch(() => {}).finally(() => { running--; drain(); });
    }
  }
  function enqueue(image) {
    if (!visible.has(image) || !image.complete) return;
    const key = sourceKey(image);
    if (!key) return;
    if (pending.get(image) === key) { if (fitted.has(image)) applyFit(image); return; }
    pending.set(image, key);
    fitted.delete(image);
    image.style.removeProperty('--ww-photo-scale'); image.style.removeProperty('--ww-photo-x'); image.style.removeProperty('--ww-photo-y');
    queue.push({ image, key }); drain();
  }
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { visible.add(entry.target); enqueue(entry.target); observer.unobserve(entry.target); }
  }, { rootMargin: '250px' }) : null;
  function track(image) {
    if (!known.has(image)) { known.add(image); if (observer) observer.observe(image); else visible.add(image); }
    enqueue(image);
  }
  function scan(root) {
    if (!(root instanceof Element) && root !== document) return;
    if (root instanceof Element && root.matches(selector)) track(root);
    root.querySelectorAll(selector).forEach(track);
  }
  function start() {
    scan(document);
    document.addEventListener('load', event => { if (event.target instanceof HTMLImageElement && event.target.matches(selector)) track(event.target); }, true);
    new MutationObserver(records => { for (const record of records) for (const node of record.addedNodes) if (node instanceof Element) scan(node); }).observe(document.body, { childList: true, subtree: true });
    if (!resize) window.addEventListener('resize', () => document.querySelectorAll(selector).forEach(applyFit), { passive: true });
  }
  start();
})();

(() => {
  'use strict';
  const selector = '.ww-card-pricing', watched = new WeakSet(), widths = new WeakMap(), pending = new Set();
  let frame = 0;
  function fit(wrapper) {
    const row = wrapper.querySelector('.ww-card-price-line');
    if (!row || !row.clientWidth) return;
    row.style.removeProperty('--ww-card-price-size');
    const style = getComputedStyle(row), size = parseFloat(style.fontSize), gap = parseFloat(style.columnGap) || 0;
    const pieces = [...row.children].filter(node => !node.classList.contains('t4s-price__unit') && node.getBoundingClientRect().width);
    const gaps = gap * Math.max(0, pieces.length - 1);
    const needed = Math.max(row.scrollWidth, pieces.reduce((sum, node) => sum + node.getBoundingClientRect().width, 0) + gaps);
    if (needed > row.clientWidth + .5) {
      const fitted = Math.max(10, Math.floor(size * (row.clientWidth - gaps) / Math.max(1, needed - gaps) * 10) / 10 - .1);
      row.style.setProperty('--ww-card-price-size', fitted + 'px');
    }
  }
  function queue(wrapper) {
    if (!wrapper || !wrapper.isConnected) return;
    pending.add(wrapper);
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; for (const item of pending) if (item.isConnected) fit(item); pending.clear(); });
  }
  const resize = 'ResizeObserver' in window ? new ResizeObserver(entries => {
    for (const entry of entries) {
      const width = entry.target.clientWidth;
      if (widths.get(entry.target) !== width) { widths.set(entry.target, width); queue(entry.target); }
    }
  }) : null;
  function track(wrapper) {
    if (!watched.has(wrapper)) { watched.add(wrapper); if (resize) resize.observe(wrapper); }
    queue(wrapper);
  }
  function scan(root) {
    if (!(root instanceof Element) && root !== document) return;
    if (root instanceof Element) { if (root.matches(selector)) track(root); else queue(root.closest(selector)); }
    root.querySelectorAll(selector).forEach(track);
  }
  scan(document);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.target instanceof Element) queue(record.target.closest(selector));
      else queue(record.target.parentElement?.closest(selector));
      for (const node of record.addedNodes) if (node instanceof Element) scan(node);
      if (resize) for (const node of record.removedNodes) if (node instanceof Element) {
        const removed = node.matches(selector) ? [node, ...node.querySelectorAll(selector)] : [...node.querySelectorAll(selector)];
        for (const wrapper of removed) if (!wrapper.isConnected) { resize.unobserve(wrapper); watched.delete(wrapper); }
      }
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  window.addEventListener('resize', () => document.querySelectorAll(selector).forEach(queue), { passive: true });
  if (document.fonts) document.fonts.ready.then(() => document.querySelectorAll(selector).forEach(queue));
})();
